/**
 * Job Progress utilities
 *
 * Maps the lifecycle status of a Work Order or Inspection to a 0-100
 * progress value plus a human-readable label. Centralized here so the
 * list rows, detail pages, and any future dashboards stay in sync as
 * statuses are added or renamed — and so the status→percent mapping
 * isn't re-invented at every call site.
 *
 * Status sets:
 *   - Work Order:  pending → in_progress → in_review → approved → completed
 *                  (→ invoiced → closed billing tail; on_hold pauses at the
 *                  in_progress value; cancelled is terminal). 'approved' is
 *                  the POST-review state — the reviewer has signed off on the
 *                  finished work — so it sits just below completed. See
 *                  WORK_ORDER_STATUSES in utils/constants.ts.
 *   - Inspection:  pending → in_progress → observations_complete →
 *                  corrections_in_progress → in_review → approved →
 *                  completed (the same review hand-off as work orders,
 *                  layered on at the review stage; see Inspection::STATUSES
 *                  in backend/app/Models/Inspection.php)
 *
 * Percent values are picked so the list-page progress bar visibly
 * advances at every stage transition and never moves backward along
 * any reachable path. They are presentational only; nothing else in
 * the system reads them.
 */

import { formatLabel } from './formatLabel';

export type JobEntityType = 'work_order' | 'inspection';

export interface JobProgress {
	/** 0-100, integer. 100 means the record is in a terminal completed state. */
	percent: number;
	/** True for any status that the UI should treat as "done". */
	isComplete: boolean;
	/** True for statuses that are terminal-not-completed (cancelled / dropped). */
	isCancelled: boolean;
	/** Title-cased label for the underlying status — same string the
	 *  status badge used. Suitable for an aria-label or tooltip. */
	label: string;
}

const WORK_ORDER_PERCENTS: Record<string, number> = {
	pending: 5,
	in_progress: 50,
	/* Paused, not regressed — same value as in_progress so putting a job
	 * on hold never visibly moves the bar. */
	on_hold: 50,
	in_review: 75,
	approved: 90,
	completed: 100,
	/* Billing tail — the work itself is done, so the bar stays full. */
	invoiced: 100,
	closed: 100,
	cancelled: 0,
};

const INSPECTION_PERCENTS: Record<string, number> = {
	pending: 5,
	scheduled: 15,
	in_progress: 30,
	observations_complete: 55,
	corrections_in_progress: 80,
	in_review: 85,
	approved: 95,
	completed: 100,
	cancelled: 0,
};

/*
 * Statuses the UI treats as "done" (the green check in list rows). Work
 * orders stay done through the post-completion billing tail — an invoiced
 * or closed job must not regress to an in-flight bar (jobAction.ts routes
 * the same set to view-only for the same reason). Inspections have no
 * such tail, and their `approved` state precedes `completed`, so only
 * `completed` counts.
 */
const WORK_ORDER_COMPLETED_STATUSES = new Set(['completed', 'invoiced', 'closed']);
const INSPECTION_COMPLETED_STATUSES = new Set(['completed']);

/**
 * Resolve a status string to the unified JobProgress shape.
 *
 * Falls back to 0% when the status isn't in the table — better to
 * render an empty bar than to over-state progress on an unknown value.
 */
export function getJobProgress(status: string | null | undefined, type: JobEntityType): JobProgress {
	const safe = status ?? '';
	const isInspection = type === 'inspection';
	const map = isInspection ? INSPECTION_PERCENTS : WORK_ORDER_PERCENTS;
	const completedStatuses = isInspection ? INSPECTION_COMPLETED_STATUSES : WORK_ORDER_COMPLETED_STATUSES;
	return {
		percent: map[safe] ?? 0,
		isComplete: completedStatuses.has(safe),
		isCancelled: safe === 'cancelled',
		label: formatLabel(safe),
	};
}
